import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MapPin, Facebook, Youtube, Instagram } from 'lucide-react';
const navLinks = [{
  to: '/about',
  label: 'About'
}, {
  to: '/classes',
  label: 'Classes'
}, {
  to: '/schedule',
  label: 'Schedule'
}, {
  to: '/resources',
  label: 'Resources'
}, {
  to: '/blog',
  label: 'Blog'
}, {
  to: '/proof',
  label: 'Proof'
}];
interface NavigationMobileMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
export function NavigationMobileMenu({
  isOpen,
  setIsOpen
}: NavigationMobileMenuProps) {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  return <>
      {/* Mobile Menu Button */}
      <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 text-[#1e3a8a] hover:bg-gray-100 rounded-lg transition-colors" aria-label="Toggle menu" data-id="element-115">
        {isOpen ? <X className="w-6 h-6" data-id="element-116" /> : <Menu className="w-6 h-6" data-id="element-117" />}
      </button>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-118">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/50 z-[60] lg:hidden" data-id="element-119" />

            {/* Menu Panel */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed right-0 top-0 bottom-0 w-64 bg-white shadow-2xl z-[70] lg:hidden overflow-y-auto" data-id="element-120">
              {/* Close button in menu */}
              <div className="flex justify-between items-center p-4 border-b border-gray-100" data-id="element-121">
                <span className="font-black text-xl text-[#1e3a8a]" data-id="element-122">Menu</span>
                <button onClick={() => setIsOpen(false)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-123">
                  <X className="w-5 h-5" data-id="element-124" />
                </button>
              </div>

              <div className="p-6 space-y-1" data-id="element-125">
                <Link to="/" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${isActive('/') ? 'bg-[#1e3a8a] text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-126">
                  Home
                </Link>

                {navLinks.map(link => <Link key={link.to} to={link.to} onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${isActive(link.to) ? 'bg-[#1e3a8a] text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-127">
                    {link.label}
                  </Link>)}

                {/* Contact CTA in Mobile Menu */}
                <Link to="/contact" onClick={() => setIsOpen(false)} className="block px-4 py-3 mt-4 bg-[#dc2626] text-white rounded-lg font-semibold hover:bg-red-700 transition-colors text-center border-2 border-[#a3d977]/30" data-id="element-128">
                  Contact Us
                </Link>

                {/* Address in Mobile Menu */}
                <a href="https://www.google.com/maps/place/3815+196th+St+SW+%23109,+Lynnwood,+WA+98036" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 px-4 py-3 mt-4 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors" data-id="element-129">
                  <MapPin className="w-5 h-5 text-[#1e3a8a] shrink-0 mt-0.5" data-id="element-130" />
                  <div className="text-sm leading-tight" data-id="element-131">
                    <div className="font-semibold" data-id="element-132">3815 196th St SW #109</div>
                    <div data-id="element-133">Lynnwood, WA 98036</div>
                  </div>
                </a>

                {/* Social Links in Mobile Menu */}
                <div className="pt-6 mt-6 border-t border-gray-200" data-id="element-134">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-4" data-id="element-135">
                    Follow Us
                  </p>
                  <div className="flex gap-4 px-4" data-id="element-136">
                    <a href="https://www.facebook.com/NwBlackBeltAcademy/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 text-[#1e3a8a] hover:text-[#0f1f3d] transition-colors" aria-label="Facebook" data-id="element-137">
                      <Facebook className="w-6 h-6" data-id="element-138" />
                    </a>
                    <a href="https://www.youtube.com/channel/UCVz3NW22j-95JDLAvEWXrHw" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 text-[#1e3a8a] hover:text-[#dc2626] transition-colors" aria-label="YouTube" data-id="element-139">
                      <Youtube className="w-6 h-6" data-id="element-140" />
                    </a>
                    <a href="https://www.instagram.com/nw_black_belt_academy/?hl=en" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 text-[#1e3a8a] hover:text-[#0f1f3d] transition-colors" aria-label="Instagram" data-id="element-141">
                      <Instagram className="w-6 h-6" data-id="element-142" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}